/**
 * 「消耗明细」的展示规则（契约 §3.4 `GET /quota/ledger` 与 `/quota/ledger/conversations`，v1.3.0）。
 *
 * 单独成文件而不是写在 `QuotaView` 模板里，理由与 `ledger-owner.ts` 相同：
 * 这里每一句文案都对应一个**判断**（空态、截断、轮次、未关联会话），
 * 而 `vitest` 不转 `.vue` —— 规则留在模板里就等于没有测试。
 *
 * 归属判定**复用** `describeLedgerOwner()`：按会话聚合与逐次明细必须用同一套规则，
 * 否则同一个会话在两个视图里会一个叫标题、一个叫「会话已删除」。
 *
 * v1.3.0 起契约不再下发任何 token 量：按会话只给 `sharePercent`（服务端算好的
 * 占本期消耗百分比），逐次只给 `kind`。所以这个模块里**没有任何换算**——
 * 它负责的只有措辞与归属。
 */

import type { ConversationUsage, ConversationUsageResponse, LedgerEntry } from '@spirit/contracts';
import { formatDateTime } from './format';
import { describeLedgerOwner, type LedgerOwnerView } from './ledger-owner';

/** 聚合行（模板里不再做任何判断） */
export interface UsageRow {
  /** `v-for` 的 key：`conversationId` 可能为 null，单独给一个不会与 id 冲突的值 */
  key: string;
  owner: LedgerOwnerView;
  /** 该会话占本期消耗的百分比（服务端算好的 0-100 整数），不是 token 量。 */
  sharePercent: number;
  rounds: number;
  /** 该会话窗口内最后一次消耗的时间 */
  lastAt: string;
}

/** `conversationId === null` 那组的行 key（真实 id 是 cuid2，绝不会等于这个值） */
export const UNLINKED_USAGE_KEY = '__unlinked__';

export function usageRowsOf(
  response: ConversationUsageResponse,
  /** 会话 id → 标题，来自用户自己的会话列表 */
  titles: ReadonlyMap<string, string>,
  /** 会话列表是否**完整**拉取过（决定能不能说"会话已删除"） */
  indexComplete: boolean,
): UsageRow[] {
  return response.items.map((item: ConversationUsage) => ({
    key: item.conversationId ?? UNLINKED_USAGE_KEY,
    owner: describeLedgerOwner(item.conversationId, titles, indexComplete),
    sharePercent: item.sharePercent,
    rounds: item.rounds,
    lastAt: item.lastAt,
  }));
}

/**
 * 账本行的性质。
 *
 * 旧实现靠"金额为负"来判返还——契约 v1.3.0 删掉了金额，`kind` 才是唯一真源。
 * 逐次明细目前只会收到 `commit`（服务端只回结算行），其余几种保留是因为
 * 契约把它们定义成了合法值：写不出文案会让将来真的出现该行时显示成空白。
 *
 * `grant`（v1.4.0 新增）是 ROOT 在后台手动发放额度产生的行：它不写 Redis 计数器，
 * 而是在月度口径里做减项——用户侧可用量 = 额度上限 −（已 commit − 已 grant）。
 * 前台逐次明细仍然只会收到 `commit`，这个分支同样是类型穷尽 + 将来真出现该行时不显示空白。
 * **刻意不写 `default:`**：契约是唯一真源，将来再加 kind 时编译器要当场报错，
 * 而不是让新值静默进入某条既有分支。
 */
export function describeLedgerKind(kind: LedgerEntry['kind']): string {
  switch (kind) {
    case 'commit':
      return '计入消耗';
    case 'reserve':
      return '预扣中';
    case 'release':
      return '已返还';
    case 'grant':
      // 只说性质，不带任何绝对 token 量——前台不展示点数（契约 v1.3.0 起的硬约束）。
      return '额度发放';
  }
}

/** 「2026-10-01 08:00 起至 2026-11-01 07:59」——窗口右端是重置瞬间（排他），说清"起至"而不是"到" */
export function describeUsageWindow(response: Pick<ConversationUsageResponse, 'windowStart' | 'windowEnd'>): string {
  return `${formatDateTime(response.windowStart)} 起至 ${formatDateTime(response.windowEnd)}`;
}

/** 「3 轮」；0 轮是异常数据，照实说而不是显示空白 */
export function describeUsageRounds(rounds: number): string {
  return `${rounds} 轮`;
}

/** 「共 4 个会话」——`itemCount` 是窗口内的分组总数，截断时它仍是真值 */
export function describeUsageItemCount(itemCount: number): string {
  return `共 ${itemCount} 个会话`;
}

/** 「合计 4 轮」。份额已经在每一行上，这里只报轮次，不再有一个容易对不上的总额。 */
export function describeUsageTotals(rounds: number): string {
  return `合计 ${describeUsageRounds(rounds)}`;
}

/** 「最近 2026-10-06 03:03」 */
export function describeUsageLastAt(iso: string): string {
  return `最近 ${formatDateTime(iso)}`;
}

/** 某一行的右侧文案：占比，而不是绝对量。 */
export function describeUsageShare(sharePercent: number): string {
  return `${sharePercent}%`;
}

/**
 * 截断提示。
 *
 * 列表只装得下前 `shown` 个会话 —— 不说明这一点，用户会拿列表里的几行去对合计，
 * 然后发现"加不起来"。文案**不说总数**（`items` 是截断后的切片），只说"其余未显示"。
 */
export function describeUsageTruncation(truncated: boolean, shown: number): string | null {
  if (!truncated) {
    return null;
  }
  return `仅显示占比最高的 ${shown} 个会话，其余未显示（合计仍是全部会话的真值）。`;
}

/** 空态文案：区分"还没用过"与"这个窗口内没用过" */
export function describeUsageEmpty(windowText: string): string {
  return `这段时间还没有消耗记录（${windowText}）。`;
}

